import {
  AddressDisplay,
  Alert,
  Badge,
  Button,
  cn,
  Glass,
  Input,
  Skeleton,
  toast,
  Tooltip,
} from "@gotts.ai/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { encodeFunctionData, isAddress, parseUnits } from "viem";

import { AnimatedBalance } from "../components/AnimatedBalance";
import { TokenIcon } from "../components/TokenIcon";
import { useDeployment, useEvmBackend, useWallet } from "../context";
import { useDevenvNavigate } from "../hooks/useDevenvNavigate";
import { useRpcRequest } from "../hooks/useRpcRequest";
import { useTokenBalances } from "../hooks/useTokenBalances";
import { MOCK_ERC20_ABI, V3_NFT_POSITION_MANAGER_ABI } from "../lib/abis";
import { TOKEN_DECIMALS, TOKEN_SYMBOLS } from "../lib/constants";
import {
  formatBalanceCompact,
  formatPortfolioPercent,
  formatRelativeTime,
  formatUsd,
  formatUsdCompact,
} from "../lib/format";
import { getTokenBg } from "../lib/token-colors";

import type { Address } from "viem";

// ---------------------------------------------------------------------------
// Reference prices for approximate USD values
// ---------------------------------------------------------------------------

const USD_PRICES: Record<string, number> = {
  ETH: 3000,
  WETH: 3000,
  USDC: 1,
  USDT: 1,
  DAI: 1,
  WBTC: 60000,
};

const STABLECOIN_SET = new Set(["USDC", "USDT", "DAI"]);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type SortMode = "value" | "name" | "type";

function tokenSortKey(
  symbol: string,
  usd: number,
  mode: SortMode,
): string | number {
  if (mode === "value") return -usd; // descending
  if (mode === "name") return symbol;
  // "type": stablecoins first, native second, volatile third
  const group = STABLECOIN_SET.has(symbol) ? "0" : symbol === "ETH" ? "1" : "2";
  return `${group}-${symbol}`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SummaryCard({
  label,
  value,
  loading,
  onClick,
  className,
}: {
  label: string;
  value: React.ReactNode;
  loading?: boolean;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <Glass
      className={cn(
        "glass-card flex-1 px-4 py-3",
        onClick && "cursor-pointer",
        className,
      )}
    >
      <div onClick={onClick} className={onClick ? "contents" : undefined}>
        <div className="section-label">{label}</div>
        <div className="stat-number mt-1 text-sm">
          {loading ? <Skeleton width={60} height={16} /> : value}
        </div>
      </div>
    </Glass>
  );
}

function HeroSummaryCard({
  value,
  delta,
  loading,
}: {
  value: React.ReactNode;
  delta?: { amount: number; label: string };
  loading?: boolean;
}) {
  const hideDelta = delta && Math.abs(delta.amount) > 0 && loading !== true;
  return (
    <Glass className="glass-card gold-edge px-5 py-4">
      <div className="hero-stat-label">Portfolio value</div>
      <div className="hero-stat mt-1">
        {loading ? <Skeleton width={180} height={32} /> : value}
      </div>
      {hideDelta && delta.amount !== 0 && (
        <div
          className={cn(
            "mt-1 font-mono text-[11px]",
            delta.amount > 0
              ? "text-[var(--color-success)]"
              : "text-[var(--color-error)]",
          )}
        >
          {delta.amount > 0 ? "+" : ""}
          {formatUsdCompact(Math.abs(delta.amount))}
          <span className="ml-1 text-[9px] text-[var(--color-text-muted)]">
            {delta.label}
          </span>
        </div>
      )}
    </Glass>
  );
}

/** Default mint amounts per token */
const MINT_AMOUNTS: Record<string, string> = {
  WETH: "10",
  USDC: "10000",
  USDT: "10000",
  DAI: "10000",
  WBTC: "1",
};

// Inline SVG icons for action buttons (12px)
function MintIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="inline-block"
    >
      <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M6 3.5V8.5M3.5 6H8.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SwapIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="inline-block"
    >
      <path
        d="M2 4H10M10 4L8 2M10 4L8 6"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 8H2M2 8L4 6M2 8L4 10"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LpIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="inline-block"
    >
      <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M6 3.5V8.5M3.5 6H8.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface TokenCardProps {
  symbol: string;
  address: Address | null;
  balance: bigint;
  decimals: number;
  formatted: string;
  usdValue: number;
  price: number;
  portfolioPct: string;
  hidePct?: boolean;
  error?: string;
  loading?: boolean;
  isNative?: boolean;
  onMint?: (customAmount?: string) => void;
  minting?: boolean;
  onNavigateSwap?: () => void;
  onNavigateLp?: () => void;
}

function TokenCard({
  symbol,
  address,
  balance,
  formatted,
  usdValue,
  price,
  portfolioPct,
  hidePct,
  error,
  loading,
  isNative,
  onMint,
  minting,
  onNavigateSwap,
  onNavigateLp,
}: TokenCardProps) {
  const [customAmount, setCustomAmount] = useState("");
  const [mintOpen, setMintOpen] = useState(false);
  const isZero = balance === 0n && !loading;
  const defaultMint = MINT_AMOUNTS[symbol] ?? "100";
  const parsedValue = parseFloat(formatted);
  const compact = formatBalanceCompact(parsedValue, symbol);

  return (
    <Glass
      className={cn(
        "glass-card-interactive token-card-hover p-5 transition-opacity",
        isZero && !error && "token-card-zero opacity-50",
      )}
    >
      {/* Header row: icon + symbol + optional badges + % pill */}
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2">
          <TokenIcon symbol={symbol} size={32} />
          <span className="font-mono text-[13px] font-bold text-[var(--color-text)]">
            {symbol}
          </span>
          {symbol === "WETH" && (
            <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
              = 1:1 ETH
            </span>
          )}
          {isNative && <Badge variant="info">Native</Badge>}
        </span>
        {!hidePct && (
          <span className="pct-pill font-mono text-[var(--color-text-muted)]">
            {portfolioPct}
          </span>
        )}
      </div>

      {/* Balance row — compact with full on hover */}
      <div className="mt-3">
        {loading ? (
          <Skeleton width={140} height={24} />
        ) : error ? (
          <span className="font-mono text-xl text-[var(--color-error)]">
            --
          </span>
        ) : (
          <Tooltip content={compact.full}>
            <span
              className={cn(
                "font-mono text-xl font-bold",
                balance > 0n
                  ? "text-[var(--color-text)]"
                  : "text-[var(--color-text-muted)]",
              )}
            >
              {compact.display} {symbol}
            </span>
          </Tooltip>
        )}
      </div>

      {/* USD value row — price in tooltip */}
      {!loading && !error && (
        <Tooltip content={price > 0 ? `${formatUsd(price)}/${symbol}` : ""}>
          <div className="mt-1 font-mono text-[13px] text-[var(--color-text-muted)]">
            {formatUsd(usdValue)}
          </div>
        </Tooltip>
      )}

      {/* Address row */}
      {address && (
        <div
          className={cn("mt-2 flex items-center gap-1", isZero && "opacity-70")}
        >
          <span className="font-mono text-[9px] text-[var(--color-text-muted)]">
            Contract
          </span>
          <AddressDisplay
            address={address}
            leadChars={6}
            tailChars={4}
            fontSize={isZero ? 9 : 10}
          />
        </div>
      )}
      {isNative && !address && (
        <div className="mt-2 font-mono text-[10px] text-[var(--color-text-muted)]">
          Native currency
        </div>
      )}

      {/* Error row */}
      {error && (
        <div className="mt-1 text-[10px] text-[var(--color-error)]">
          {error}
        </div>
      )}

      {/* Actions row */}
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {onMint && !mintOpen && (
          <Button
            variant={isZero ? "gold" : "ghost"}
            size="sm"
            onClick={() => {
              setMintOpen(true);
            }}
            className="font-mono text-[10px]"
          >
            <span className="flex items-center gap-1">
              <MintIcon />
              Mint
            </span>
          </Button>
        )}
        {onNavigateSwap &&
          (balance > 0n ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onNavigateSwap}
              className="font-mono text-[10px]"
            >
              <span className="flex items-center gap-1">
                <SwapIcon />
                Swap
              </span>
            </Button>
          ) : (
            <Tooltip content="Mint first">
              <Button
                variant="ghost"
                size="sm"
                disabled
                className="font-mono text-[10px]"
              >
                <span className="flex items-center gap-1">
                  <SwapIcon />
                  Swap
                </span>
              </Button>
            </Tooltip>
          ))}
        {onNavigateLp &&
          (balance > 0n ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onNavigateLp}
              className="font-mono text-[10px]"
            >
              <span className="flex items-center gap-1">
                <LpIcon />
                Add LP
              </span>
            </Button>
          ) : (
            <Tooltip content="Mint first">
              <Button
                variant="ghost"
                size="sm"
                disabled
                className="font-mono text-[10px]"
              >
                <span className="flex items-center gap-1">
                  <LpIcon />
                  Add LP
                </span>
              </Button>
            </Tooltip>
          ))}
      </div>

      {/* Collapsible mint form */}
      {onMint && (
        <div className="mint-expand" data-open={mintOpen}>
          <div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <Input
                type="number"
                placeholder={defaultMint}
                value={customAmount}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                }}
                className="h-6 w-20 font-mono text-[10px]"
              />
              <Button
                variant={isZero ? "gold" : "ghost"}
                size="sm"
                onClick={() => {
                  onMint(customAmount || undefined);
                  setCustomAmount("");
                }}
                loading={minting}
                disabled={minting}
                className="font-mono text-[10px]"
              >
                <span className="flex items-center gap-1">
                  <MintIcon />
                  {minting
                    ? "Minting..."
                    : `Mint ${customAmount || defaultMint} ${symbol}`}
                </span>
              </Button>
              <button
                onClick={() => {
                  setMintOpen(false);
                }}
                className="font-mono text-[10px] text-[var(--color-text-muted)] underline"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </Glass>
  );
}

// ---------------------------------------------------------------------------
// Portfolio Composition Bar
// ---------------------------------------------------------------------------

function CompositionBarChart({
  data,
  total,
  defaultExcludeDominant,
}: {
  data: { name: string; usd: number }[];
  total: number;
  defaultExcludeDominant?: boolean;
}) {
  const [excludeDominant, setExcludeDominant] = useState(
    defaultExcludeDominant ?? false,
  );

  const first = data.length > 0 ? data[0] : undefined;
  const dominant = first && first.usd / total > 0.9 ? first : null;
  const filtered =
    excludeDominant && dominant
      ? data.filter((d) => d.name !== dominant.name)
      : data;
  const filteredTotal = filtered.reduce((s, d) => s + d.usd, 0);

  return (
    <div>
      {/* Bar — taller at h-10 (40px) */}
      <div className="flex h-10 w-full overflow-hidden rounded-sm">
        {filtered.map((entry) => {
          const pct = filteredTotal > 0 ? (entry.usd / filteredTotal) * 100 : 0;
          if (pct < 0.1) return null;
          return (
            <Tooltip
              key={entry.name}
              content={`${entry.name}: ${formatUsd(entry.usd)} (${formatPortfolioPercent(entry.usd, total)})`}
            >
              <div
                style={{
                  width: `${String(pct)}%`,
                  backgroundColor: getTokenBg(entry.name),
                }}
                className="h-full min-w-[3px] transition-all duration-300"
              />
            </Tooltip>
          );
        })}
      </div>

      {/* Dominant toggle */}
      {dominant && (
        <button
          onClick={() => {
            setExcludeDominant((v) => !v);
          }}
          className="mt-2 font-mono text-[10px] text-[var(--color-text-muted)] underline decoration-dotted"
        >
          {excludeDominant
            ? "Show all assets"
            : `Focus on smaller positions (exclude ${dominant.name})`}
        </button>
      )}

      {/* Legend — 2-column grid */}
      <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
        {data.map((entry) => {
          const pct = total > 0 ? (entry.usd / total) * 100 : 0;
          const isDominant = entry.name === dominant?.name;
          const isNegligible = pct < 1;
          return (
            <div
              key={entry.name}
              className={cn(
                "flex items-center gap-1.5 font-mono text-xs",
                isNegligible && "opacity-40",
              )}
            >
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ backgroundColor: getTokenBg(entry.name) }}
              />
              <TokenIcon symbol={entry.name} size={20} />
              <span
                className={cn(
                  "text-[var(--color-text)]",
                  isDominant && "font-bold",
                )}
              >
                {entry.name}
              </span>
              <span className="text-[var(--color-text-muted)]">
                {formatUsdCompact(entry.usd)}
              </span>
              <span className="text-[var(--color-text-muted)]">
                {formatPortfolioPercent(entry.usd, total)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toggle Switch
// ---------------------------------------------------------------------------

function ToggleSwitch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (val: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => {
        onChange(!checked);
      }}
      className="flex cursor-pointer items-center gap-1.5"
    >
      <span className="toggle-switch" aria-checked={checked}>
        <span className="toggle-switch-knob" />
      </span>
      <span>{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main View
// ---------------------------------------------------------------------------

export default function Balances() {
  const navigate = useDevenvNavigate();
  const { address } = useWallet();
  const { balances, ethBalance, loading, connectionError, errors, refetch } =
    useTokenBalances();
  const { deployment } = useDeployment();
  const { accounts, mintToken } = useEvmBackend();
  const { request } = useRpcRequest();
  const [mintingToken, setMintingToken] = useState<string | null>(null);
  const [v3PositionCount, setV3PositionCount] = useState<number>(0);
  const [sortBy, setSortBy] = useState<SortMode>("value");
  const [hideZero, setHideZero] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedAddr, setCopiedAddr] = useState<string | null>(null);
  const lastRefreshRef = useRef(Date.now());
  const [, setTick] = useState(0);
  const initialTotalRef = useRef<number | null>(null);

  // Auto-tick every 1s for "Updated Xs ago"
  useEffect(() => {
    const id = setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, []);

  // Fetch V3 position count
  useEffect(() => {
    if (!address || !deployment?.v3.nonfungiblePositionManager) return;
    const npm = deployment.v3.nonfungiblePositionManager;
    const calldata = encodeFunctionData({
      abi: V3_NFT_POSITION_MANAGER_ABI,
      functionName: "balanceOf",
      args: [address],
    });
    void request("eth_call", [{ to: npm, data: calldata }, "latest"])
      .then((result: unknown) => {
        if (typeof result === "string" && result && result !== "0x") {
          setV3PositionCount(Number(BigInt(result)));
        }
      })
      .catch(() => {
        // Silently ignore — position count is best-effort
      });
  }, [address, deployment, request]);

  // Build unified token list (ETH + ERC-20)
  const allTokens = useMemo(() => {
    const items: {
      symbol: string;
      address: Address | null;
      balance: bigint;
      decimals: number;
      formatted: string;
      usdValue: number;
      price: number;
      isNative: boolean;
    }[] = [];

    // ETH as first item
    const ethRaw = ethBalance?.raw ?? 0n;
    const ethFormatted = ethBalance?.formatted ?? "0";
    const ethPrice = USD_PRICES.ETH ?? 0;
    items.push({
      symbol: "ETH",
      address: null,
      balance: ethRaw,
      decimals: 18,
      formatted: ethFormatted,
      usdValue: parseFloat(ethFormatted) * ethPrice,
      price: ethPrice,
      isNative: true,
    });

    // ERC-20 tokens — only include tokens with valid deployed addresses
    for (const symbol of TOKEN_SYMBOLS) {
      const tokenKey = symbol.toLowerCase() as keyof NonNullable<
        typeof deployment
      >["tokens"];
      const tokenAddress =
        (deployment?.tokens?.[tokenKey] as Address | undefined) ?? null;

      // Skip tokens with invalid addresses (e.g. "0x" placeholders from fork mode)
      if (tokenAddress && !isAddress(tokenAddress)) continue;

      const bal = balances[symbol];
      const price = USD_PRICES[symbol] ?? 0;
      const formatted = bal?.formatted ?? "0";
      items.push({
        symbol,
        address: tokenAddress,
        balance: bal?.raw ?? 0n,
        decimals: TOKEN_DECIMALS[symbol] ?? 18,
        formatted,
        usdValue: parseFloat(formatted) * price,
        price,
        isNative: false,
      });
    }

    return items;
  }, [ethBalance, balances, deployment]);

  // Portfolio totals
  const totalUsd = useMemo(
    () => allTokens.reduce((sum, t) => sum + t.usdValue, 0),
    [allTokens],
  );

  // Track initial total for delta indicator
  useEffect(() => {
    if (initialTotalRef.current === null && totalUsd > 0) {
      initialTotalRef.current = totalUsd;
    }
  }, [totalUsd]);

  const portfolioDelta =
    initialTotalRef.current !== null ? totalUsd - initialTotalRef.current : 0;

  // Stablecoins breakdown
  const stablecoinsBreakdown = useMemo(
    () =>
      allTokens
        .filter((t) => STABLECOIN_SET.has(t.symbol))
        .map((t) => ({ symbol: t.symbol, usd: t.usdValue })),
    [allTokens],
  );
  const stablecoinsUsd = useMemo(
    () => stablecoinsBreakdown.reduce((sum, t) => sum + t.usd, 0),
    [stablecoinsBreakdown],
  );

  // Portfolio composition data (only non-zero, sorted desc)
  const portfolioData = useMemo(
    () =>
      allTokens
        .filter((t) => t.balance > 0n)
        .map((t) => ({ name: t.symbol, usd: t.usdValue }))
        .sort((a, b) => b.usd - a.usd),
    [allTokens],
  );

  // Whether a dominant token exists (>90% of portfolio)
  const hasDominant = useMemo(() => {
    const first = portfolioData[0];
    return (
      portfolioData.length > 0 &&
      first !== undefined &&
      first.usd / totalUsd > 0.9
    );
  }, [portfolioData, totalUsd]);

  // Whether a single token dominates >99% (hide allocation pills)
  const hasDominantOver99 = useMemo(
    () =>
      portfolioData.length > 0 &&
      totalUsd > 0 &&
      (portfolioData[0]?.usd ?? 0) / totalUsd > 0.99,
    [portfolioData, totalUsd],
  );

  // Count of zero-balance tokens for filter label
  const zeroCount = useMemo(
    () => allTokens.filter((t) => t.balance === 0n).length,
    [allTokens],
  );

  // Sorted + filtered list for the grid
  const displayTokens = useMemo(() => {
    let list = hideZero ? allTokens.filter((t) => t.balance > 0n) : allTokens;
    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(
        (t) =>
          t.symbol.toLowerCase().includes(q) ||
          (t.address?.toLowerCase().includes(q) ?? false),
      );
    }
    list = [...list].sort((a, b) => {
      const ka = tokenSortKey(a.symbol, a.usdValue, sortBy);
      const kb = tokenSortKey(b.symbol, b.usdValue, sortBy);
      if (typeof ka === "number" && typeof kb === "number") return ka - kb;
      return String(ka).localeCompare(String(kb));
    });
    return list;
  }, [allTokens, sortBy, hideZero, searchQuery]);

  const totalTokenCount = allTokens.length;
  const displayCount = displayTokens.length;

  // Check if all tokens are zero-balance
  const allZero = useMemo(
    () => allTokens.every((t) => t.balance === 0n),
    [allTokens],
  );

  // Error lookup
  const errorMap: Record<string, string> = {};
  for (const e of errors) {
    errorMap[e.token] = e.message;
  }
  const hasErrors = errors.length > 0;

  // Timestamp staleness
  const secondsSinceRefresh = Math.floor(
    (Date.now() - lastRefreshRef.current) / 1000,
  );

  const handleRefresh = useCallback(() => {
    refetch();
    lastRefreshRef.current = Date.now();
  }, [refetch]);

  function handleCopyAddress(addr: string) {
    void navigator.clipboard
      .writeText(addr)
      .then(() => {
        setCopiedAddr(addr);
        setTimeout(() => {
          setCopiedAddr(null);
        }, 2000);
      })
      .catch(() => {
        // Fallback — ignore in test/SSR environments
      });
  }

  async function handleQuickMint(symbol: string, customAmount?: string) {
    if (!address || !deployment?.tokens) return;
    // Native ETH doesn't need minting via ERC-20
    if (symbol === "ETH") return;
    const tokenKey = symbol.toLowerCase() as keyof typeof deployment.tokens;
    const tokenAddress = deployment.tokens[tokenKey] as Address | undefined;
    if (!tokenAddress || !isAddress(tokenAddress)) return;

    setMintingToken(symbol);
    try {
      const amount = customAmount ?? MINT_AMOUNTS[symbol] ?? "100";
      const decimals = TOKEN_DECIMALS[symbol] ?? 18;
      const parsed = parseUnits(amount, decimals);

      if (mintToken) {
        // Use context-provided mint (storage slot for fork, impersonation for scratch)
        await mintToken(tokenAddress, address, parsed);
      } else {
        // Fallback: impersonated deployer calling mint() (devenv compatibility)
        const calldata = encodeFunctionData({
          abi: MOCK_ERC20_ABI,
          functionName: "mint",
          args: [address, parsed],
        });

        const firstAccount = accounts[0];
        if (!firstAccount) return;
        const deployer = firstAccount.address;
        await request("anvil_impersonateAccount", [deployer]);
        await request("eth_sendTransaction", [
          { from: deployer, to: tokenAddress, data: calldata },
        ]);
        await request("anvil_stopImpersonatingAccount", [deployer]);
      }

      // Estimate new balance for the toast
      const currentBal = allTokens.find((t) => t.symbol === symbol);
      const currentFormatted = currentBal
        ? parseFloat(currentBal.formatted)
        : 0;
      const newApprox = currentFormatted + parseFloat(amount);
      const { display: approxDisplay } = formatBalanceCompact(
        newApprox,
        symbol,
      );
      toast.success(
        `Minted ${amount} ${symbol} \u2192 Balance: ~${approxDisplay} ${symbol}`,
      );
      refetch();
    } catch (err) {
      toast.error(
        `Mint failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setMintingToken(null);
    }
  }

  // Hide delta when uninformative (delta/total > 99% means first session)
  const showDelta =
    portfolioDelta !== 0 &&
    initialTotalRef.current !== null &&
    totalUsd > 0 &&
    Math.abs(portfolioDelta) / totalUsd < 0.99;

  if (!address) {
    return (
      <div>
        <Alert variant="warning" title="No wallet connected">
          Connect a wallet to view balances.
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 1. Action buttons */}
      <div className="flex flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-3">
          <Tooltip
            content={`Mint: ${Object.entries(MINT_AMOUNTS)
              .map(([s, a]) => `${a} ${s}`)
              .join(", ")}`}
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void (async () => {
                  for (const [symbol, amount] of Object.entries(MINT_AMOUNTS)) {
                    await handleQuickMint(symbol, amount);
                  }
                })();
              }}
              disabled={loading}
            >
              Mint All
            </Button>
          </Tooltip>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
          >
            <span className="flex items-center gap-1">
              <span>{"\u21BB"}</span>
              Refresh
            </span>
          </Button>
          <span
            className={cn(
              "font-mono text-[10px]",
              secondsSinceRefresh > 60
                ? "animate-stale-pulse text-[var(--color-warning)]"
                : "text-[var(--color-text-muted)]",
            )}
          >
            Updated {formatRelativeTime(lastRefreshRef.current)}
          </span>
        </div>
      </div>

      {/* 2. Error/empty alerts */}
      {connectionError && (
        <Alert variant="error" title="Anvil Unreachable">
          Cannot connect to Anvil. Is it running?
        </Alert>
      )}
      {hasErrors && !connectionError && (
        <Alert variant="warning" title="Some balances failed to load">
          {errors.map((e) => `${e.token}: ${e.message}`).join("; ")}
        </Alert>
      )}
      {allZero && !loading && (
        <Alert variant="warning" title="No tokens yet">
          Your local Anvil chain is ready. Click Mint All to fund your account
          with test tokens.
        </Alert>
      )}

      {/* 3. Hero portfolio value (full width) */}
      <HeroSummaryCard
        value={
          <AnimatedBalance
            value={totalUsd}
            prefix="$"
            decimals={0}
            compact={true}
            emDash={false}
          />
        }
        delta={
          showDelta
            ? { amount: portfolioDelta, label: "since last snapshot" }
            : undefined
        }
        loading={loading}
      />

      {/* 4. Secondary stats (2-col) */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SummaryCard
          label="Stablecoins"
          value={
            <div>
              <span>{formatUsdCompact(stablecoinsUsd)}</span>
              {stablecoinsBreakdown.length > 0 && (
                <div className="mt-1 font-mono text-[10px] text-[var(--color-text-muted)]">
                  {stablecoinsBreakdown
                    .map((s) => `${s.symbol} ${formatUsdCompact(s.usd)}`)
                    .join(" + ")}
                </div>
              )}
            </div>
          }
          loading={loading}
        />
        <SummaryCard
          label="LP Positions"
          value={
            <div className="flex items-center justify-between">
              <span className="flex items-baseline gap-2">
                <span>{v3PositionCount}</span>
                <span className="text-xs text-[var(--color-text-muted)]">
                  position{v3PositionCount !== 1 ? "s" : ""}
                </span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigate("liquidity");
                }}
                className="font-mono text-[10px]"
              >
                View Positions
              </Button>
            </div>
          }
        />
      </div>

      {/* 5. Portfolio composition chart */}
      {portfolioData.length > 0 && (
        <Glass className="glass-card p-5">
          <div className="section-tag">Portfolio composition</div>
          <CompositionBarChart
            data={portfolioData}
            total={totalUsd}
            defaultExcludeDominant={hasDominant}
          />
        </Glass>
      )}

      {/* 6. Sort & filter controls */}
      <div className="flex flex-wrap items-center gap-4 font-mono text-xs text-[var(--color-text-muted)]">
        <label className="flex items-center gap-1.5">
          Sort:
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value as SortMode);
            }}
            className="rounded border border-[var(--glass-border)] bg-[var(--color-surface)] px-2 py-1 font-mono text-[11px] text-[var(--color-text)]"
          >
            <option value="value">{"\u2193"} Highest Value</option>
            <option value="name">{"\u2193"} By Name (A-Z)</option>
            <option value="type">{"\u2193"} By Type</option>
          </select>
        </label>
        <ToggleSwitch
          checked={hideZero}
          onChange={setHideZero}
          label={
            hideZero && zeroCount > 0
              ? `Hide zero balances (${String(zeroCount)} hidden)`
              : "Hide zero balances"
          }
        />
        <Input
          type="text"
          placeholder="Search tokens..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
          className="h-6 w-36 rounded border border-[var(--glass-border)] bg-[var(--color-surface)] px-2 font-mono text-[11px] text-[var(--color-text)]"
        />
        <span>
          {displayCount === totalTokenCount
            ? `${String(totalTokenCount)} tokens`
            : `${String(displayCount)} of ${String(totalTokenCount)} tokens`}
        </span>
      </div>

      {/* 7. Token card grid */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {displayTokens.map((token) => (
          <TokenCard
            key={token.symbol}
            symbol={token.symbol}
            address={token.address}
            balance={token.balance}
            decimals={token.decimals}
            formatted={token.formatted}
            usdValue={token.usdValue}
            price={token.price}
            portfolioPct={formatPortfolioPercent(token.usdValue, totalUsd)}
            hidePct={hasDominantOver99}
            error={errorMap[token.symbol]}
            loading={loading}
            isNative={token.isNative}
            onMint={
              token.symbol === "ETH"
                ? undefined
                : (customAmount) =>
                    void handleQuickMint(token.symbol, customAmount)
            }
            minting={mintingToken === token.symbol}
            onNavigateSwap={() => {
              navigate(`swap?tokenIn=${token.symbol}`);
            }}
            onNavigateLp={() => {
              navigate(`liquidity?token=${token.symbol}`);
            }}
          />
        ))}
      </div>

      {/* 8. LP Positions panel */}
      <div className="section-divider">
        <Glass className="glass-card p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="section-label">LP Positions</span>
              <Badge variant="info">V3</Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                navigate("liquidity");
              }}
            >
              View Positions
            </Button>
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <AnimatedBalance
              value={v3PositionCount}
              integer
              emDash={false}
              className="text-2xl font-bold text-[var(--color-text)]"
            />
            <span className="text-text-muted text-xs">
              position{v3PositionCount !== 1 ? "s" : ""}
            </span>
          </div>
          <p className="mt-2 text-[11px] text-[var(--color-text-muted)]">
            {v3PositionCount === 0
              ? "No LP positions yet. Add liquidity to start earning fees."
              : "View position details, fee earnings, and in-range status on the Liquidity page."}
          </p>
        </Glass>
      </div>

      {/* 9. Deployment addresses (collapsible) */}
      {deployment?.tokens && (
        <details className="group">
          <summary className="section-label cursor-pointer">
            Deployment addresses
            <span className="ml-1.5 text-[10px] text-[var(--color-text-muted)]">
              (ERC-20 Mocks)
            </span>
          </summary>
          <Glass className="glass-card mt-2 p-4">
            <table className="w-full text-xs">
              <tbody>
                {Object.entries(deployment.tokens)
                  .filter(([, addr]) => isAddress(addr as string))
                  .map(([sym, addr]) => (
                    <tr
                      key={sym}
                      className="border-b border-[var(--glass-border)]"
                    >
                      <td className="py-1.5 font-mono font-medium text-[var(--color-text)]">
                        <span className="inline-flex items-center gap-1.5">
                          <TokenIcon symbol={sym.toUpperCase()} size={16} />
                          {sym.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-1.5">
                        <AddressDisplay
                          address={addr as Address}
                          leadChars={10}
                          tailChars={8}
                          fontSize={11}
                          copyable
                          copied={copiedAddr === (addr as string)}
                          onCopy={() => {
                            handleCopyAddress(addr as string);
                          }}
                        />
                      </td>
                      <td className="py-1.5 text-right">
                        <button
                          onClick={() => {
                            navigate("contracts");
                          }}
                          className="font-mono text-[10px] text-[var(--color-gold)] underline"
                        >
                          View Contract
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </Glass>
        </details>
      )}
    </div>
  );
}
